import { PlayCircleFilledOutlined } from "@mui/icons-material";
import { Box, Button, styled, Typography, useTheme } from "@mui/material";
import StoreCheckbox from "components/StoreCheckbox";
import Section from "components/Section";
import Subsection from "components/Subsection";
import { useEffect, useState } from "react";
import useStore from "hooks/useStore";

export default function Documents() {
    const errors = useStore((state) => state.formErrors);
    const [isDeclarationOfConsentVisible, setIsDeclarationOfConsentVisible] = useState(true);

    const toggleIsDeclarationOfConsentVisible = () => {
        setIsDeclarationOfConsentVisible(!isDeclarationOfConsentVisible);
    };

    useEffect(() => {
        if (errors.declarationOfConsentAccept_2 || errors.termsAndConditionsAccept) {
            setIsDeclarationOfConsentVisible(true);
        }
    }, [errors]);

    return (
        <Section title="Documents" number={4} errorMessage="Please click the required checkbox.">
            <Subsection>
                <Typography>Please read the following documents carefully and confirm that you have done so. Thank you very much.</Typography>
            </Subsection>
            <Subsection>
                <Button onClick={toggleIsDeclarationOfConsentVisible} variant="outlined">
                    <PlayCircleFilledOutlined color="primary" style={isDeclarationOfConsentVisible ? { transform: "rotate(90deg)" } : {}} />
                    <Typography fontWeight="bold" variant="button">
                        &nbsp;Declaration of Consent
                    </Typography>
                </Button>
                <Box mt={2} mb={2} padding={2} sx={{ border: "1px solid gray" }} style={{ display: isDeclarationOfConsentVisible ? "block" : "none" }}>
                    <DeclarationOfConsent />
                </Box>
            </Subsection>
        </Section>
    );
}

function DeclarationOfConsent() {
    const theme = useTheme();

    return (
        <StyledDeclarationOfConsent>
            <Typography variant="h2" sx={{ color: theme.palette.text.primary, textAlign: "center" }} mb={4}>
                Declaration of Consent
            </Typography>
            <Typography paragraph>
                The above-designated person (hereinafter &ldquo;student&rdquo;) declares his/her perusal, acknowledgement, and consent that the regulations, house
                rules, legal directions, and transfers of rights which are dealt with in the following, amend:
            </Typography>
            <StyledUl>
                <li>
                    <Typography>
                        the Study Contract between the student and George Emil Palade University of Medicine, Pharmacy, Science, and Technology of Targu Mures
                        (hereinafter &ldquo;UMFST&rdquo;), and
                    </Typography>
                </li>
                <li>
                    <Typography>the Service Contract between the student and CPE Europe GmbH (hereinafter &ldquo;campus management&rdquo;).</Typography>
                </li>
            </StyledUl>

            <Typography fontWeight="bold" paragraph>
                I. General Regulation
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        The student &mdash; as a participant of the human medicine study program at the UMFST&apos;s Hamburg branch, the University Targu Mures
                        Medical Campus Hamburg (hereinafter &ldquo;UMFST-UMCH&rdquo;) &mdash; confirms the acknowledgment and perusal of:
                    </Typography>
                    <StyledUl>
                        <li>
                            <Typography>the house rules of UMFST-UMCH,</Typography>
                        </li>
                        <li>
                            <Typography>
                                the security and safety regulations of UMFST-UMCH; moreover, the student agrees to attend the necessary safety briefings
                                offered either by UMFST-UMCH or the campus management (either digitally or in person), and
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                the student commits himself/herself to pursue all regulations and rules which are mentioned in paragraph 1.
                            </Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>
                        The student proves to the campus management &mdash; at the latest by the time of the commencing of his/her study program at
                        UMFST-UMCH &mdash; that he/she maintains a health insurance, accident insurance, and personal liability insurance valid throughout the
                        EU with sufficient coverage (according to point C.II. of the CPE Service Contract &ndash; &ldquo;Health, Accident and Liability
                        Insurance&rdquo;).
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        In addition, for bedside teaching in teaching hospitals, at the start of the third academic year of study at the latest, the
                        applicant/student proves to CPE without being requested to do so that they hold professional liability insurance valid throughout the
                        EU, covering medical activities carried out under the supervision and/or instruction of a duly qualified doctor.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                II. Conduct on the (virtual) Campus
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        The student pledges himself/herself to behave courteously and respectfully towards all his/her fellow students, the instructors, and the
                        representatives of UMFST-UMCH/the campus management.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        All forms of discrimination &mdash; regardless of whether they take place on the grounds of origin, gender, sexual orientation/identity,
                        age, or because of any other reason &mdash; are strictly forbidden on campus, in the e-learning environment as well as in any other
                        (digital) platform/medium which is operated at UMFST-UMCH (or by the campus management), and &mdash; generally &mdash; in the context
                        of the study program offered at UMFST-UMCH. The student obliges himself/herself to respect and to obey this provision and all
                        anti-discrimination regulations at UMFST-UMCH.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The student commits himself/herself to apply the necessary care and due diligence when using premises, facilities, equipment,
                        installations, as well as any other kind of moveable and immovable goods which are owned by UMFST-UMCH and/or the campus operator. In
                        the event of damages (or losses) of the aforementioned objects, the student is obliged to notify immediately either UMFST-UMCH or the
                        campus management about the damage(s)/loss(es) and to cover the costs incurring as a result.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The student obliges himself/herself to peruse, acknowledge, and follow all rules and regulations concerning the use of chats, forums, and
                        all other digital communications channels which are provided by UMFST-UMCH or the campus management in the context of the
                        above-mentioned study program (as well as of any extracurricular activities).
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                III. Study-Relevant Behaviour
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        The student commits himself/herself to perform all study-relevant assignments and to produce all relevant pieces of work (exams,
                        term/seminar papers, quizzes, homework, tests, etc.) independently, personally, and without any help by others. He/she fully respects
                        all requirements concerning good scientific practice.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The student declares to obey all rights of third parties (especially copyrights, licenses, rights to use, third parties&apos; rights to
                        their own image/word, etc.) when performing study-relevant assignments and/or producing relevant pieces of work (exams, term/seminar
                        papers, quizzes, homework, tests, etc.) in the context of the above-mentioned study program at UMFST-UMCH.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The student obliges himself/herself to use all kind of study materials (books, texts, scripts, exams, etc.) to which he/she gets access
                        due to his/her participation in the above-mentioned study program at UMFST-UMCH only and exclusively for private educational purposes.
                        Additionally, the student agrees not to share, to distribute, or to publish any of these materials.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                VI. Additional Contractual Terms
            </Typography>
            <Typography paragraph>
                In case that any provision in this agreement shall be invalid, illegal or unenforceable, the validity, legality and enforceability of the
                remaining provisions shall not in any way be affected or impaired thereby and such provision shall be ineffective only to the extent of such
                invalidity, illegality or unenforceability.
            </Typography>
            <StoreCheckbox
                name="termsAndConditionsAccept"
                label="I agree to the aforementioned terms and conditions."
                required
                color="primary"
                sx={{ marginBottom: 4 }}
            />

            <Typography variant="h2" sx={{ color: theme.palette.text.primary, textAlign: "center" }} mb={4} mt={4}>
                Privacy, Recording of Teaching Activities, and Consent to the Use of Photo, Video, and Audio Recordings
            </Typography>

            <Typography fontWeight="bold" paragraph>
                1. Processing of Personal Data in Connection with Studies
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        As part of the English-language Medicine degree program at UMFST, students&apos; personal data are processed to the extent necessary for
                        the implementation, organization, and administration of the academic program and for the provision of related academic services.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>This may include, in particular, the following categories of personal data:</Typography>
                    <StyledUl>
                        <li>
                            <Typography>Basic personal data, including name and contact details;</Typography>
                        </li>
                        <li>
                            <Typography>
                                Academic data, including information regarding course participation, academic performance, and examination results;
                            </Typography>
                        </li>
                        <li>
                            <Typography>Organizational and administrative data required for the administration of the academic program;</Typography>
                        </li>
                        <li>
                            <Typography>
                                Photo, video, and audio recordings, insofar as such recordings are created in connection with teaching activities or other
                                study-related activities.
                            </Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>
                        The data controllers within the meaning of the General Data Protection Regulation (GDPR) are the entities identified in the applicable
                        privacy notices. Where CPE Europe GmbH processes personal data in connection with the provision of UMFST academic services, such
                        processing is carried out only on the basis of an applicable legal basis under Article 6 GDPR and exclusively for specified and
                        legitimate purposes. Processing is limited to what is necessary for the respective purpose.
                    </Typography>
                    <StyledUl>
                        <li>
                            <Typography>
                                Where processing is necessary for the performance of a contract between the data subject and the respective contractual partner
                                or for the implementation of pre-contractual measures, such processing is based on Article 6(1)(b) GDPR.
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                Where personal data are processed to comply with legal obligations, such processing is based on Article 6(1)(c) GDPR.
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                Where processing is necessary for the purposes of the legitimate interests pursued by CPE Europe GmbH or UMFST, and where such
                                interests are not overridden by the interests, fundamental rights, or freedoms of the data subject, processing may be based on
                                Article 6(1)(f) GDPR. This may include, in particular, the processing and temporary retention of documents and records where
                                necessary for the establishment, exercise, or defense of legal claims or for the documentation of legally relevant matters.
                            </Typography>
                        </li>
                    </StyledUl>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                2. Recording of Teaching Activities
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Selected teaching activities, lectures, courses, and other study-related events conducted as part of studies at UMFST-UMCH, whether in
                        person or digitally, may be recorded in the form of photo, video, and audio recordings.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>Such recordings may be used in particular to:</Typography>
                    <StyledUl>
                        <li>
                            <Typography>Enable students to attend and/or review teaching activities;</Typography>
                        </li>
                        <li>
                            <Typography>Provide teaching and learning content through designated e-learning and communication platforms;</Typography>
                        </li>
                        <li>
                            <Typography>Document the delivery and quality of academic instruction;</Typography>
                        </li>
                        <li>
                            <Typography>
                                Fulfill quality assurance, accreditation, or academic documentation requirements where an appropriate legal basis exists.
                            </Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>
                        Recordings may be made available through e-learning and communication platforms used by UMFST-UMCH, including Canvas, as well as through
                        other systems necessary for the academic program.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>Under Law No. 199/2023, UMFST has the statutory mandate to provide higher education.</Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The recording is intended to enable students to access the course content after the in-person class. In particular:
                    </Typography>
                    <StyledUl>
                        <li>
                            <Typography>Review,</Typography>
                        </li>
                        <li>
                            <Typography>Follow-up study,</Typography>
                        </li>
                        <li>
                            <Typography>Exam preparation,</Typography>
                        </li>
                        <li>
                            <Typography>Making up missed classes,</Typography>
                        </li>
                        <li>
                            <Typography>Closing knowledge gaps.</Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>
                        This is directly connected to the university&apos;s statutory educational mission. In addition, the recording serves to ensure the
                        quality of instruction and provides meaningful documentation for accreditation procedures and internal quality controls.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Access permissions shall depend on the specific purpose of the recording. As a general rule, recordings shall be made accessible only to
                        persons whose access is necessary to fulfill the relevant purpose.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                3. Visibility of Students in Teaching Activity Recordings
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Where students are identifiable in a recording of a teaching activity or where their voices are audible in an audio recording, this may
                        constitute the processing of personal data.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Such processing shall occur only where an appropriate legal basis exists. Where consent is required, it shall be obtained separately and
                        voluntarily.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                4. Recordings at Events and Other Activities
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Photo, video, and audio recordings may be created during events and other activities associated with UMFST-UMCH, including, in
                        particular, extracurricular events, workshops, fairs, or similar activities.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Where an individual is identifiable in such recordings or where their statements are recorded, processing shall be carried out on the
                        applicable data protection legal basis, including, where required, on the basis of separately obtained consent pursuant to Article
                        6(1)(a) GDPR.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                5. Use and Publication of Recordings for PR and Communication Purposes
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Where separate consent has been granted, photo, video, and audio recordings in which the consenting individual is identifiable, or in
                        which the individual&apos;s voice or statements have been recorded, may be processed and published for the communication, public
                        relations, and PR purposes expressly identified in the consent.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>This may include publication in the following media:</Typography>
                    <StyledUl>
                        <li>
                            <Typography>Print publications and other informational materials;</Typography>
                        </li>
                        <li>
                            <Typography>Digital information and communication media;</Typography>
                        </li>
                        <li>
                            <Typography>Newsletters;</Typography>
                        </li>
                        <li>
                            <Typography>UMFST-UMCH websites and the websites of the entities responsible for such purposes;</Typography>
                        </li>
                        <li>
                            <Typography>Official social media channels.</Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>Such social media platforms may include, in particular:</Typography>
                    <StyledUl>
                        <li>
                            <Typography>Instagram;</Typography>
                        </li>
                        <li>
                            <Typography>Facebook;</Typography>
                        </li>
                        <li>
                            <Typography>LinkedIn;</Typography>
                        </li>
                        <li>
                            <Typography>YouTube;</Typography>
                        </li>
                        <li>
                            <Typography>TikTok;</Typography>
                        </li>
                        <li>
                            <Typography>Reddit.</Typography>
                        </li>
                    </StyledUl>
                </li>
                <li>
                    <Typography paragraph>
                        The specific accounts and online offerings currently in use shall be identified in the applicable privacy notices and/or the relevant
                        consent declaration.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                6. Editing of Recordings
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        To the extent necessary for the purposes described above, photo, video, and audio recordings may be technically edited, including by
                        cropping, compiling, formatting, post-processing, or combining them with other content.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>As part of the technical implementation, attention will be paid to the following aspects:</Typography>
                    <StyledUl>
                        <li>
                            <Typography>Camera focused exclusively on the instructor;</Typography>
                        </li>
                        <li>
                            <Typography>Presentation/whiteboard;</Typography>
                        </li>
                        <li>
                            <Typography>No groups of students in the frame;</Typography>
                        </li>
                        <li>
                            <Typography>No separate recording of the audio in the classroom;</Typography>
                        </li>
                        <li>
                            <Typography>Access limited to the respective cohort;</Typography>
                        </li>
                        <li>
                            <Typography>Internal university system with authentication;</Typography>
                        </li>
                        <li>
                            <Typography>Defined deletion periods.</Typography>
                        </li>
                    </StyledUl>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                7. Rights of Use
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Where a data subject consents to the use of photo, video, or audio recordings, such consent shall be limited exclusively to the purposes
                        and types of use expressly described in this declaration and/or the relevant consent declaration.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        No ownership rights, copyrights, or other rights in the underlying recordings are transferred to the data subject by virtue of such
                        consent.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        The granting of consent does not entitle the data subject to compensation unless such compensation has been expressly agreed upon.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                8. Retention Period and Deletion
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        Personal data and recordings shall be retained only for as long as necessary for the relevant purpose or as required by a statutory
                        retention obligation or another legal basis permitting continued retention.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>Different retention periods may apply to different processing purposes.</Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Where recordings are required for the establishment, exercise, or defense of legal claims, they may be retained for the duration
                        necessary for the relevant assessment or legal proceedings.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Once the relevant processing purpose or legal basis no longer exists, the personal data shall be deleted unless statutory retention
                        obligations or other legal grounds prevent deletion.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        No blanket or unlimited retention shall occur. Processing is subject, in particular, to the principle of storage limitation pursuant to
                        Article 5(1)(e) GDPR.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                9. Voluntary Consent for PR and Public Relations Purposes
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        The processing of personal data for public relations, communication, marketing, advertising, or other PR purposes, to the extent not
                        already permitted on another legal basis, shall be based on separate consent pursuant to Article 6(1)(a) GDPR.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Consent is voluntary. Participation in the academic program and the provision of the contractually agreed academic services shall not
                        be made conditional upon granting consent to processing that is not necessary for the conduct of the academic program. This is
                        consistent with the requirements for freely given consent under Article 7 GDPR.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Consent may be withdrawn at any time with future effect. Withdrawal shall not affect the lawfulness of processing carried out prior to
                        the withdrawal.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Upon receipt of a withdrawal request, processing for the purposes covered by the consent shall generally cease, and the relevant
                        recordings shall no longer be used for those purposes unless another legal basis exists for continued processing. Content already
                        published may not be capable of complete removal due to the technical and practical characteristics of the Internet or third-party
                        platforms.
                    </Typography>
                </li>
            </StyledOl>

            <Typography fontWeight="bold" paragraph>
                10. Consent Declaration
            </Typography>
            <StyledOl>
                <li>
                    <Typography paragraph>
                        I voluntarily consent to the processing and publication of my personal data, including photo, video, and audio recordings in which I am
                        identifiable or in which my voice or statements have been recorded, for the public relations, communication, and PR purposes of CPE
                        Europe GmbH, UMFST-UMCH, and the respective partners identified, as expressly stated in this consent.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        This consent may cover, in particular, recordings created during extracurricular events, workshops, fairs, other events, or activities
                        that are not directly required for the conduct of the academic program, whether on or off campus.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Where expressly indicated, publication may take place on websites, in digital and printed informational materials, newsletters, and
                        official social media channels.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        I understand that this consent is voluntary and that refusing to provide consent will not result in any disadvantage regarding my
                        participation in the academic program or the provision of the contractually agreed academic services.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        I have been informed that I may withdraw my consent at any time with future effect. The withdrawal may be submitted to the data
                        controller identified in the applicable privacy notices. The lawfulness of processing carried out on the basis of consent prior to its
                        withdrawal shall remain unaffected.
                    </Typography>
                </li>
                <li>
                    <Typography paragraph>
                        Further information regarding the processing of my personal data, the data controllers, recipients or categories of recipients,
                        retention periods, and my rights as a data subject is available in the applicable privacy notices provided pursuant to Articles 13 and
                        14 GDPR.
                    </Typography>
                </li>
            </StyledOl>
            <StoreCheckbox
                name="declarationOfConsentAccept_2"
                label="I voluntarily consent to the processing and publication of my personal data as described above"
                color="primary"
                sx={{ marginBottom: 2 }}
            />
        </StyledDeclarationOfConsent>
    );
}

const StyledDeclarationOfConsent = styled("div")(({ theme }) => ({
    fontSize: theme.typography.body1.fontSize,
}));

const StyledUl = styled("ul")({
    listStyleType: "square",
});

const StyledOl = styled("ol")({});
